import { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { Alert, Button, Spin } from 'antd';
import { Timestamp } from 'firebase/firestore';
import PropTypes from 'prop-types';

import Card from 'containers/App/Auth/components/Card';

import IntlMessages from 'util/IntlMessages';

import ConfirmUserForm from '../ConfirmUserForm';

import {
  isInviteRequiresSignInError,
  isOnboardingAlreadyCompletedError,
  resolveAcceptInvitationErrorMessage,
} from '../../utils/resolveAcceptInvitationErrorMessage';

const toInvitation = (context, fallback) => ({
  email: context.email,
  firstName: context.firstName ?? '',
  lastName: context.lastName ?? '',
  idCard: context.idCard ?? '',
  birthDate: context.birthDate ?? null,
  phone: context.phone ?? '',
  flexTime: context.flexTime ?? false,
  professionalEmail: context.professionalEmail ?? '',
  timeZone: context.timeZone ?? '',
  address: context.address ?? null,
  organizationCountry: context.organizationCountry ?? '',
  organizationId: context.organizationId ?? fallback.organizationId,
  organizationName: context.organizationName ?? '',
  userId: context.userId ?? fallback.userId,
  organizations: Array.isArray(context.organizations) ? context.organizations : [],
});

/**
 * Onboarding form shared by the invite link (/invitationV2) and the signed-in fallback
 * (/complete-registration). Loading failures replace the form; submit failures keep it
 * mounted with what the invitee typed so they can fix the field and retry.
 *
 * `signedIn` is true when the invitee already has a session: an "already active" answer then
 * reloads the app instead of sending them to sign in again.
 *
 * An invite for an existing account carries no personal data and cannot be redeemed: the card
 * asks the invitee to sign in (`onSignInRequired`).
 */
const InviteOnboardingCard = ({
  loadContext,
  submit,
  skipPassword = false,
  signedIn = false,
  titleId,
  intro,
  onSignInRequired,
  fallback = {},
}) => {
  const intl = useIntl();
  const [invitation, setInvitation] = useState(null);
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [signInRequired, setSignInRequired] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const context = await loadContext();
        if (cancelled) return;
        if (!context?.email) {
          setLoadError({ message: intl.formatMessage({ id: 'users.invitation.accept.badToken' }) });
          return;
        }
        if (context.requiresSignIn) {
          setSignInRequired({ email: context.email, name: context.organizationName ?? '' });
          return;
        }
        setInvitation(toInvitation(context, fallback));
      } catch (error) {
        if (cancelled) return;
        if (isInviteRequiresSignInError(error)) {
          setSignInRequired({ email: '', name: '' });
          return;
        }
        const alreadyActive = isOnboardingAlreadyCompletedError(error);
        setLoadError({
          alreadyActive,
          message:
            alreadyActive && signedIn
              ? intl.formatMessage({ id: 'users.completeRegistration.alreadyActive' })
              : resolveAcceptInvitationErrorMessage(intl, error),
        });
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSign = async values => {
    const {
      password,
      firstName = '',
      lastName = '',
      idCard = '',
      birthDate,
      phone = '',
      address,
      jurisdictionId,
    } = values || {};

    setSubmitError('');
    setSubmitting(true);
    try {
      await submit({
        password,
        organizationId: invitation.organizationId,
        userId: invitation.userId,
        user: {
          firstName,
          lastName,
          idCard,
          birthDate: birthDate
            ? Timestamp.fromDate(birthDate instanceof Date ? birthDate : new Date(birthDate))
            : undefined,
          phone,
          flexTime: invitation.flexTime,
          professionalEmail: invitation.professionalEmail || invitation.email,
          timeZone: invitation.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          address,
          jurisdictionId,
        },
      });
    } catch (error) {
      if (isInviteRequiresSignInError(error)) {
        setSignInRequired({ email: '', name: invitation.organizationName });
        setInvitation(null);
      } else {
        setSubmitError(resolveAcceptInvitationErrorMessage(intl, error));
      }
      setSubmitting(false);
    }
  };

  return (
    <Card
      footer={
        <div className="gx-px-5">
          <IntlMessages id="users.invitation.accept.footer" />
        </div>
      }
    >
      {loadError && (
        <Alert
          type={loadError.alreadyActive ? 'info' : 'error'}
          message=""
          description={
            <>
              {loadError.message}
              {loadError.alreadyActive && (
                <div className="gx-mt-2">
                  {signedIn ? (
                    // Full reload so the profile and org user are read again.
                    <Button type="primary" onClick={() => window.location.assign('/')}>
                      <IntlMessages id="users.completeRegistration.continue" />
                    </Button>
                  ) : (
                    <Link to="/signin">
                      <IntlMessages id="users.invitation.accept.signIn" />
                    </Link>
                  )}
                </div>
              )}
            </>
          }
        />
      )}
      {signInRequired && (
        <Alert
          type="info"
          message=""
          description={
            <>
              <IntlMessages
                id="users.invitation.accept.requiresSignIn"
                values={{ email: signInRequired.email || '—', name: signInRequired.name || '—' }}
              />
              <div className="gx-mt-2">
                {onSignInRequired && (
                  <Button type="primary" onClick={onSignInRequired}>
                    <IntlMessages id="users.invitation.accept.signInToAccept" />
                  </Button>
                )}
              </div>
            </>
          }
        />
      )}
      {checking && (
        <Spin>
          <Alert
            message={<IntlMessages id="users.invitation.accept.loading" />}
            type="info"
          />
        </Spin>
      )}
      {!checking && !loadError && invitation && (
        <>
          {intro && <Alert className="gx-mb-3" message="" description={intro} type="info" />}
          {!!submitError && (
            <Alert className="gx-mb-3" description={submitError} message="" type="error" showIcon />
          )}
          <ConfirmUserForm
            email={invitation.email}
            loading={submitting}
            onSign={onSign}
            firstName={invitation.firstName}
            lastName={invitation.lastName}
            idCard={invitation.idCard}
            birthDate={invitation.birthDate}
            phone={invitation.phone}
            address={invitation.address}
            organizationCountry={invitation.organizationCountry}
            organizationName={invitation.organizationName}
            titleId={titleId}
            skipPassword={skipPassword}
            orgs={invitation.organizations}
          />
        </>
      )}
    </Card>
  );
};

InviteOnboardingCard.propTypes = {
  loadContext: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  skipPassword: PropTypes.bool,
  signedIn: PropTypes.bool,
  titleId: PropTypes.string,
  intro: PropTypes.node,
  onSignInRequired: PropTypes.func,
  fallback: PropTypes.shape({
    organizationId: PropTypes.string,
    userId: PropTypes.string,
  }),
};

export default InviteOnboardingCard;
